import { BRACKET_MATERIALS } from 'constant/enums';
import { jsonData } from 'constant/jsonData';
import { RecommendedBracketsData } from 'constant/recommendedBracketsData';
import { imgsData } from 'utils/imageData';

/**
 * This o object has names of all the dropdowns for bracket calculator
 * these are in sync with JSON file provide as jsonData
 * It is to facilitate mapping and conversions of dropdown label (JSON key) and,
 * API names
 */
export const DropdownNames = {
  roofType: 'Roof Type',
  constructionType: 'Construction Type',
  snowBracketType: 'Snow Bracket Type',
  snowBracketMaterial: 'Snow Bracket Material',
  roofSheathingType: 'Sheathing',
  fastenerType: 'Fastener Type',
  mountTo: 'Mount To',
  installationMethod: 'Installation Method',
  manufacturer: 'Manufacturer'
};

/**
 * Similar to above DropdownNames object following is for Numeirc inputs
 */
export const inputNames = {
  groundSnowLoad: 'Ground Snow Load',
  rainLoad: 'Rain Load',
  roofSlope: 'Roof Pitch / Slope',
  verticalExposure: 'Vertical Exposure of Product',
  horizontalExposure: 'Horizontal Exposure of Product',
  numberOfRoofingSqrs: 'Number of Roofing Squares'
};

/**
 * To show lables according to the key of outputs from redux store state
 */
export const outputLabels = {
  reqSnowBrktPerSqrArg: 'Required Snow Brackets per Square:',
  actualSnowBracketPerSquare: 'Calculated/Actual Snow Bracket per Square:',
  horizontalEaveSpacing: 'Horizontal Eave Spacing (in):',
  horizontalFieldSpacing: 'Horizontal Field Spacing (in):',
  verticalSpacing: 'Vertical Spacing (in):',
  numOfCoursesHorizontalAtEave: 'Number of Courses Horizontally at Eave:',
  numOfCoursesVertically: 'Number of Courses Vertically:',
  totalBracketsForRoofingSquares: 'Total Number of Brackets' // update after client answers
};

/**
 *
 * @param {*} value
 * @param {object} object
 * @returns key of the given object that has given value
 */
const getKeyByValue = (value, object) => {
  if (!object) return '';
  return Object.keys(object).find((key) => object[key] == value);
};

/**
 *
 * @param {object} object
 * @returns object containing only those keys which have valid value
 * current valid values are those except null and empty
 */
export const getObjectWithValidValues = (object) => {
  const filteredData = Object.fromEntries(
    Object.entries(object)?.filter(([_, value]) => {
      let val = value;
      if (typeof value == 'object' && value !== null) val = value?.value;
      return val !== null && val !== '';
    })
  );
  return filteredData;
};

/**
 * to get nested object if key is a combination of several names concatinated with |
 *
 * @param {string} singleKey This is the value (key in jsonData) of current dropdown
 * @param {object} object This is the current object conatining nested object/s
 * @returns {object} nested object found agains provided key
 */
const getValueByConcatinatedKey = (singleKey, object) => {
  try {
    const matchingKey = Object.keys(object)?.find((key) => {
      const splitedKeys = key?.split('|')?.map((key) => key);
      if (splitedKeys?.includes(singleKey)) return key;
    });
    // get the nested object based on mathced key
    return object[matchingKey];
  } catch (err) {
    console.error(err);
  }
};

// this map will be identical to the map used to store bracket dropdowns options selected
const map = new Map();
// to store inputs results
let resultantInputs = {};
let imgsDataArr = [];

/**
 *
 * @param {string} inputsString mostly a long string of concatinated input field names
 * @param {object} inputsObj inputs object from API response
 * @returns {object} have values for input fileds of bracket calculator
 */
const getInputsObject = (inputsString, inputsObj) => {
  resultantInputs = {};
  const inputsArr = inputsString.split('|');
  const filteredInputsObj = getObjectWithValidValues(inputsObj);
  for (const input of inputsArr) {
    const key = getKeyByValue(input, inputNames);
    resultantInputs[key] = filteredInputsObj[key];
  }
  return resultantInputs;
};

/**
 *
 * @param {*} mapKey represents level of dropdown and position in the map
 * @param {*} name drops down lable as in jsonData
 * @param {*} value value of the drop down used as key in jsonData
 * @param {*} object nested object conatining all the info for next dropdowns to be rendered
 */
const updateMap = (mapKey, name, value, object) => {
  map.set(mapKey, {
    name,
    value,
    object
  });
};

const updateImgsData = (name) => {
  const imgData = imgsData.find((img) => img.tag === name);
  imgsDataArr = [...imgsDataArr, imgData];
};

/**
 * this is the main function to fill up all the neccessory data
 * to store for bracket calculator
 *
 * @param {object} inputsResponseObj this is the object we created from api response
 * @param {*} DropdwonsResponseObj this is the object we created from api response
 * @returns {object} containing all the neccessory values for bracketOptionSlice
 */
export const getQuotaionDataForEdit = (inputsResponseObj, DropdwonsResponseObj) => {
  const filteredDropdownsObj = getObjectWithValidValues(DropdwonsResponseObj);
  const filteredInputsObj = getObjectWithValidValues(inputsResponseObj);
  const keysCount = Object?.keys(filteredDropdownsObj)?.length;
  let mainObj = jsonData;
  let mapKey = 0;
  imgsDataArr = [];

  do {
    if (mapKey === 0) {
      // update map for the first value
      updateMap(
        mapKey,
        'Roof Type',
        filteredDropdownsObj[DropdownNames.roofType],
        mainObj[filteredDropdownsObj[DropdownNames.roofType]]
      );
      updateImgsData(filteredDropdownsObj[DropdownNames.roofType]);
      // next nested object for selected key
      mainObj = mainObj[filteredDropdownsObj[DropdownNames.roofType]];
      // skipping iterration
      continue;
    }

    const currentLabel = mainObj?.label;
    if (currentLabel == 'End') {
      const inputsString = Object.keys(mainObj)?.find((key) => key !== 'label');
      const resultantMap = new Map([...map]);
      const result = {
        inputs: getInputsObject(inputsString, filteredInputsObj),
        dropdowns: resultantMap,
        currentLevel: mapKey,
        currentData: mainObj,
        imgsDataArr: imgsDataArr
      };
      map.clear();
      return result;
    }
    const currentDropdown = getKeyByValue(currentLabel, DropdownNames);

    const name = currentLabel;
    // value of current dropdown
    const value = filteredDropdownsObj[DropdownNames[currentDropdown]];
    // if key of main object has mutlipal options
    const currentObj = getValueByConcatinatedKey(value, mainObj);
    mainObj = currentObj;

    updateMap(mapKey, name, value, currentObj);
    updateImgsData(value);
  } while (++mapKey <= keysCount);

  return 'error';
};

const getSortedArray = (sortByMaterial, pricesDataArr) => {
  if (!pricesDataArr?.length) {
    return [];
  }

  return pricesDataArr.sort((bracket1, bracket2) => {
    // Handle 1stRecommended and 2ndRecommended priorities
    if (bracket1['1stRecommended'] && !bracket2['1stRecommended']) {
      return -1; // bracket1 comes first
    } else if (!bracket1['1stRecommended'] && bracket2['1stRecommended']) {
      return 1; // bracket2 comes first
    }

    if (bracket1['2ndRecommended'] && !bracket2['2ndRecommended']) {
      return -1; // bracket1 comes first
    } else if (!bracket1['2ndRecommended'] && bracket2['2ndRecommended']) {
      return 1; // bracket2 comes first
    }

    // Sorting by material price
    const material1 = bracket1.materials.find((material) => sortByMaterial === material.material);
    const material2 = bracket2.materials.find((material) => sortByMaterial === material.material);

    if (!material1 && !material2) {
      return 0;
    } else if (!material1) {
      return 1;
    } else if (!material2) {
      return -1;
    }

    return material1.price - material2.price;
  });
};

const specificBrackets = {
  'Snow Bracket L': BRACKET_MATERIALS.ALUMINUM,
  'Universal Guard': BRACKET_MATERIALS.ALUMINUM,
  'Snow Bracket G 9.5 - Apex': BRACKET_MATERIALS.COPPER,
  // 'Snow Bracket G 9.5 - Classic': BRACKET_MATERIALS.COPPER,
  // 'Snow Bracket G 11.5 - Classic': BRACKET_MATERIALS.COPPER,
  // 'Snow Bracket G 11.5 - Apex': BRACKET_MATERIALS.COPPER,
  // 'Snow Bracket I (Valley) - Classic': BRACKET_MATERIALS.STEEL,
  'Snow Bracket I (Valley) - Apex': BRACKET_MATERIALS.STEEL,
  // 'Snow Bracket I (Crest) - Classic': BRACKET_MATERIALS.STEEL,
  'Snow Bracket I (Crest) - Apex': BRACKET_MATERIALS.STEEL,
  'Snow Bracket C (Mushroom)': BRACKET_MATERIALS.STEEL,
  'Snow Bracket C (Tree)': BRACKET_MATERIALS.STEEL,
  'Snow Bracket C (Moose)': BRACKET_MATERIALS.STEEL
};

// get object for image box with prices
const getImageDataWithMaterialsPrices = (imageData, pricesDataArr, roofType, constructionType) => {
  // get materials for the current bracket
  const materials =
    pricesDataArr.find((pricesObj) => pricesObj.name === imageData.title)?.materials || [];

  let filteredMaterials = materials;
  // filtering materials based on specific brackets as backend might send extra maaterials for those
  const tempFilteredMaterials = materials?.filter((material) => {
    if (imageData.title === 'Snow Bracket L' || imageData.title === 'Universal Guard') return true;
    return material.material != BRACKET_MATERIALS.ALUMINUM;
  });

  if (specificBrackets[imageData.title]) {
    if (roofType === 'Metal Shingles' || !roofType) {
      filteredMaterials = tempFilteredMaterials;
    } else {
      filteredMaterials = materials?.filter(
        (material) => material.material == specificBrackets[imageData.title]
      );
    }
  } else {
    filteredMaterials = tempFilteredMaterials;
  }
  // adding recommended property to the images
  // defaul recommended values to 0 showing absence
  const recommendedProps = {
    '1stRecommended': 0,
    '2ndRecommended': 0
  };
  try {
    let recommendedFound = RecommendedBracketsData[roofType]?.[constructionType]?.[imageData.tag];
    if (constructionType && recommendedFound) {
      const isRec1 = recommendedFound == 1;
      const isRec2 = recommendedFound == 2;
      recommendedProps['1stRecommended'] = isRec1;
      recommendedProps['2ndRecommended'] = isRec2;
    } else {
      recommendedFound = RecommendedBracketsData[roofType][imageData.tag];
      const isRec1 = recommendedFound == 1;
      const isRec2 = recommendedFound == 2;
      recommendedProps['1stRecommended'] = isRec1;
      recommendedProps['2ndRecommended'] = isRec2;
    }
  } catch (err) {
    console.warn('recommended:', err);
  }
  return {
    ...imageData,
    materials: filteredMaterials,
    ...recommendedProps
  };
};

export const getImagesDataWithMaterialsPrices = (
  imagesData,
  pricesDataArr,
  sortByMaterial,
  roofType,
  constructionType = false
) => {
  const imageDataWithPrices = imagesData?.map((imgData) => {
    return getImageDataWithMaterialsPrices(imgData, pricesDataArr, roofType, constructionType);
  });

  if (!sortByMaterial) return imageDataWithPrices;

  return getSortedArray(sortByMaterial, imageDataWithPrices);
};

export const getBracketImageWithPrice = (selectedImages, bracketImagesWithPrices) => {
  const bracketImage = selectedImages?.find((image) => image?.label === 'Snow Bracket Type');
  if (!bracketImage) return null;
  const bracketImageWithPrice = bracketImagesWithPrices.find(
    (image) => image?.title === bracketImage?.title
  );

  return bracketImageWithPrice;
};

export const removeTilde = (str) => {
  if (!str) return str;
  return str.replace(/^~+/, '');
};
